import { detect } from './detector.js';
import { getPlatform, hasAdapter, getAdapter } from './registry.js';
import * as api from './api.js';


/**
 * 创建 JXBridge 实例（依赖注入 loadAdapter）
 * @param {Function} loadAdapter 平台适配器加载函数
 * @returns {Object} JXBridge 对象
 */
export function createJXBridge({ loadAdapter }) {
  // 自动初始化（运行时探测加载适配器）
  const autoInitPromise = loadAdapter(detect())
    .catch(err => console.error('[JXBridge] init failed:', err && err.message));

  // 包装 setShare：adapter 未就绪时自动等待 ready，避免业务方踩时序坑
  function setShare(opts) {
    if (hasAdapter()) return api.setShare(opts || {});
    return autoInitPromise.then(() => {
      if (hasAdapter()) return api.setShare(opts || {});
      throw new Error('[JXBridge] adapter not ready, cannot call setShare');
    });
  }

  // 包装 share：adapter 未就绪时自动等待 ready
  // opts 必传，APP 场景下需要业务方提供分享内容
  function share(opts) {
    if (!opts || typeof opts !== 'object') {
      return Promise.reject(new Error('[JXBridge] share 必须传入分享参数'));
    }
    if (hasAdapter()) return api.share(opts);
    return autoInitPromise.then(() => {
      if (hasAdapter()) return api.share(opts);
      throw new Error('[JXBridge] adapter not ready, cannot call share');
    });
  }

  // 包装 chooseImage：adapter 未就绪时自动等待 ready
  function chooseImage(opts) {
    if (hasAdapter()) return api.chooseImage(opts || {});
    return autoInitPromise.then(() => {
      if (hasAdapter()) return api.chooseImage(opts || {});
      throw new Error('[JXBridge] adapter not ready, cannot call chooseImage');
    });
  }

  // 包装 setPageTitle：adapter 未就绪时自动等待 ready
  function setPageTitle(title) {
    if (hasAdapter()) return api.setPageTitle(title);
    return autoInitPromise.then(() => {
      if (hasAdapter()) return api.setPageTitle(title);
      throw new Error('[JXBridge] adapter not ready, cannot call setPageTitle');
    });
  }

  // 包装 closeWindow：adapter 未就绪时自动等待 ready
  function closeWindow(opts) {
    if (hasAdapter()) return api.closeWindow(opts || {});
    return autoInitPromise.then(() => {
      if (hasAdapter()) return api.closeWindow(opts || {});
      throw new Error('[JXBridge] adapter not ready, cannot call closeWindow');
    });
  }

  // 包装 getWxConfig：adapter 未就绪时自动等待 ready
  function getWxConfig(sceneCode, url, env) {
    if (hasAdapter()) return api.getWxConfig(sceneCode, url, env);
    return autoInitPromise.then(() => {
      if (hasAdapter()) return api.getWxConfig(sceneCode, url, env);
      throw new Error('[JXBridge] adapter not ready, cannot call getWxConfig');
    });
  }

  // 包装 wxConfig：adapter 未就绪时自动等待 ready
  function wxConfig(configObj, opts) {
    if (hasAdapter()) return api.wxConfig(configObj, opts);
    return autoInitPromise.then(() => {
      if (hasAdapter()) return api.wxConfig(configObj, opts);
      throw new Error('[JXBridge] adapter not ready, cannot call wxConfig');
    });
  }

  // 包装 getAgentUserInfo：adapter 未就绪时自动等待 ready
  // token 可选，未传时 fetchAgentUserInfo 内部从 cookie JX_http_usertoken 取
  function getAgentUserInfo(token, env) {
    console.warn('getAgentUserInfo的evn==========', env)
    if (hasAdapter()) return api.getAgentUserInfo(token, env);
    return autoInitPromise.then(() => {
      if (hasAdapter()) return api.getAgentUserInfo(token, env);
      throw new Error('[JXBridge] adapter not ready, cannot call getAgentUserInfo');
    });
  }

  // 包装 getFiveElements：adapter 未就绪时自动等待 ready
  // 所有平台均支持，入参 sceneCode + ecifNo
  function getFiveElements(sceneCode, ecifNo, env) {
    if (hasAdapter()) return api.getFiveElements(sceneCode, ecifNo, env);
    return autoInitPromise.then(() => {
      if (hasAdapter()) return api.getFiveElements(sceneCode, ecifNo, env);
      throw new Error('[JXBridge] adapter not ready, cannot call getFiveElements');
    });
  }

  // 获取内联的 wx 实例（仅 weixin/wxwork 平台有效）
  // 业务方用它调 wx.config / wx.ready，避免重复引入 jweixin.js
  // 必须在 JXBridge.ready 完成后调用
  function getWX() {
    const adapter = getAdapter();
    if (adapter && typeof adapter.getWX === 'function') {
      return adapter.getWX();
    }
    return null;
  }

  return {
    getPlatform,
    isReady: hasAdapter,
    ready: autoInitPromise,
    setShare,
    share,
    chooseImage,
    setPageTitle,
    closeWindow,
    getWxConfig,
    wxConfig,
    getAgentUserInfo,
    getFiveElements,
    getWX
  };
}
